<template>
  <div>
    <button class="button summary" @click="showModal">
      {{ $t('assessment.continueJourney') }}
    </button>
    <Modal
      v-model="isModalVisible"
      :title="$t('assessment.complete')"
      :dialog-class="'modal-dialog-centered modal-dialog-scrollable'"
    >
      <div v-if="assessment" class="assessment-results">
        <div class="proficiency-level">
          <div class="level-badge">{{ assessment.overallProficiency }}</div>
          <h3>{{ $t('assessment.overallLevel') }}</h3>
        </div>

        <div class="assessment-sections">
          <div class="section key-observations">
            <h4>{{ $t('assessment.keyObservations') }}</h4>
            <ul>
              <li v-for="(observation, index) in assessment.keyObservations" :key="index">
                {{ observation }}
              </li>
            </ul>
          </div>

          <div class="section recommendations">
            <h4>{{ $t('assessment.recommendations') }}</h4>
            <ul>
              <li v-for="(recommendation, index) in assessment.recommendations" :key="index">
                {{ recommendation }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div v-else class="loading-container">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">{{ $t('common.loading') }}</span>
        </div>
        <p>{{ $t('assessment.loadingResults') }}</p>
      </div>
      <template #footer>
        <div class="d-flex justify-content-center w-100">
          <button class="button continue" @click="continueToPlan">
            {{ $t('assessment.createPlan') }}
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import Modal from '../Modal.vue';
import { ref, onMounted, computed } from 'vue';

export default {
  name: 'AssessmentComplete',
  components: {
    Modal
  },
  props: {
    userId: String,
    dialogId: String,
    metadata: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const isModalVisible = ref(false);
    const assessment = computed(() => props.metadata?.assessment);

    const showModal = () => {
      isModalVisible.value = true;
    };


    return {
      isModalVisible,
      assessment,
      showModal
    };
  },
  methods: {
    continueToPlan() {
      this.isModalVisible = false;
      this.$emit('newChatRequest');
    }
  }
};
</script>

<style scoped>
:deep(.modal-title) {
  text-align: center;
  width: 100%;
}

.modal-dialog {
  max-width: 500px;
  margin: 1.75rem auto;
}

.modal-content {
  border: none;
  border-radius: 1rem;
  box-shadow: var(--al-shadow-md);
}

.modal-header {
  border-bottom: none;
  padding: 1.5rem 1.5rem 0.5rem;
}


.modal-body {
  padding: 1rem 1.5rem;
}

.modal-footer {
  border-top: none;
  padding: 0.5rem 1.5rem 1.5rem;
  justify-content: center;
}

.assessment-results {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.proficiency-level {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.proficiency-level h3 {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--al-text);
  margin: 0;
}

/* Assessment-complete is a warm/achievement moment (DESIGN.md explicitly
   lists assessment alongside achievement/upgrade for the warm accent).
   AA: white text on --al-orange is ~2.6:1 (fails); ink-900 on --al-orange
   is ~6.7:1, so the badge uses ink text instead of white. */
.level-badge {
  background-color: var(--al-orange);
  color: var(--al-ink-900);
  padding: 0.75rem 1.5rem;
  border-radius: 2rem;
  font-size: 1.25rem;
  font-weight: bold;
  box-shadow: 0 4px 6px color-mix(in srgb, var(--al-orange) 25%, transparent);
}

.assessment-sections {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.section {
  background-color: var(--al-surface-sunken);
  border-radius: 0.75rem;
  padding: 1.25rem;
  box-shadow: var(--al-shadow-sm);
}

h4 {
  color: var(--al-text);
  margin-bottom: 1rem;
  font-weight: 600;
  font-size: 1.1rem;
  position: relative;
  padding-bottom: 0.5rem;
}

/* Decorative underline only (no text sits on it): warm accent matches the
   assessment-complete theme. */
h4::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 2.5rem;
  height: 3px;
  background-color: var(--al-orange);
  border-radius: 3px;
}

ul {
  list-style-type: none;
  padding-left: 0;
  margin-bottom: 0;
}

li {
  margin-bottom: 0.75rem;
  padding-left: 1.75rem;
  position: relative;
  line-height: 1.5;
  font-size: 0.95rem;
}

li:last-child {
  margin-bottom: 0;
}

/* Checkmark bullets are small text-sized content, and --al-orange on this
   light background is ~2.6:1 (fails even the 3:1 icon bar), so they stay
   neutral dark rather than warm-accented. */
li:before {
  content: "✓";
  color: var(--al-text);
  font-weight: bold;
  position: absolute;
  left: 0;
}

/* The continue CTA stays primary blue even though the assessment content
   above is warm-themed: it's the navigational next-step action, and per
   the design map, primary CTAs are always blue. */
.button.continue {
  background-color: var(--al-primary);
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  transition: all 0.2s ease;
  width: 100%;
  max-width: 250px;
}

.button.continue:focus-visible {
  outline: 0;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--al-primary) 30%, transparent);
}

.button.continue:hover {
  background-color: var(--al-primary-hover);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px color-mix(in srgb, var(--al-primary-hover) 20%, transparent);
}

.button.continue:active {
  transform: translateY(0);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem 0;
}

/* Responsive styles */
@media (min-width: 768px) {
  .assessment-sections {
    flex-direction: row;
    gap: 1rem;
  }

  .section {
    flex: 1;
  }
}

@media (max-width: 576px) {
  .modal-dialog {
    margin: 1rem;
  }


  .level-badge {
    font-size: 1.1rem;
    padding: 0.6rem 1.2rem;
  }

  .section {
    padding: 1rem;
  }

  li {
    font-size: 0.9rem;
  }
}

.button.summary {
  background-color: var(--al-primary);
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  transition: all 0.3s ease;
  cursor: pointer;
  font-family: inherit;
}

.button.summary:focus-visible {
  outline: 0;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--al-primary) 30%, transparent);
}

.button.summary:hover {
  background-color: var(--al-primary-hover);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.button.summary:active {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transform: translateY(0);
}
</style>